<template>
  <div class="signature-pad">
    <div class="signature-canvas-wrap">
      <canvas ref="canvas" aria-label="手写签名区域"></canvas>
      <span v-if="!hasInk" class="signature-placeholder">请在框内手写本人姓名</span>
    </div>
    <div class="signature-actions">
      <span>请使用手指或触控笔签名</span>
      <button type="button" @click="clear">清除重写</button>
    </div>
  </div>
</template>

<script>
export default {
  name: "SignaturePad",
  data() {
    return {
      hasInk: false,
      drawing: false,
      points: 0,
      distance: 0,
      lastPoint: null,
      usePointerEvents: false,
      listeners: []
    };
  },
  mounted() {
    this.prepareCanvas();
    this.bindInputEvents();
    window.addEventListener("resize", this.resizeIfEmpty);
    window.addEventListener("orientationchange", this.resizeIfEmpty);
  },
  beforeDestroy() {
    this.listeners.forEach(item => item.target.removeEventListener(item.name, item.handler, item.options));
    window.removeEventListener("resize", this.resizeIfEmpty);
    window.removeEventListener("orientationchange", this.resizeIfEmpty);
  },
  methods: {
    addListener(target, name, handler, options) {
      target.addEventListener(name, handler, options);
      this.listeners.push({ target, name, handler, options });
    },
    prepareCanvas() {
      const canvas = this.$refs.canvas;
      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(1, Math.min(window.devicePixelRatio || 1, 3));
      canvas.width = Math.max(1, Math.round(rect.width * ratio));
      canvas.height = Math.max(1, Math.round(rect.height * ratio));
      const ctx = canvas.getContext("2d");
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, rect.width, rect.height);
      ctx.strokeStyle = "#172033";
      ctx.lineWidth = 2.4;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    },
    resizeIfEmpty() {
      if (!this.hasInk) {
        this.$nextTick(this.prepareCanvas);
      }
    },
    bindInputEvents() {
      const canvas = this.$refs.canvas;
      canvas.style.touchAction = "none";
      this.usePointerEvents = typeof window.PointerEvent !== "undefined";
      if (this.usePointerEvents) {
        this.addListener(canvas, "pointerdown", this.pointerStart);
        this.addListener(canvas, "pointermove", this.pointerMove);
        this.addListener(canvas, "pointerup", this.pointerEnd);
        this.addListener(canvas, "pointercancel", this.pointerEnd);
      } else {
        this.addListener(canvas, "touchstart", this.touchStart, { passive: false });
        this.addListener(canvas, "touchmove", this.touchMove, { passive: false });
        this.addListener(canvas, "touchend", this.endStroke, { passive: false });
        this.addListener(canvas, "touchcancel", this.endStroke, { passive: false });
        this.addListener(canvas, "mousedown", this.mouseStart);
        this.addListener(window, "mousemove", this.mouseMove);
        this.addListener(window, "mouseup", this.endStroke);
      }
    },
    pointFromClient(clientX, clientY) {
      const rect = this.$refs.canvas.getBoundingClientRect();
      return { x: clientX - rect.left, y: clientY - rect.top };
    },
    beginStroke(point) {
      this.drawing = true;
      this.lastPoint = point;
      const ctx = this.$refs.canvas.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    },
    drawStroke(point) {
      if (!this.drawing || !this.lastPoint) return;
      const dx = point.x - this.lastPoint.x;
      const dy = point.y - this.lastPoint.y;
      const step = Math.sqrt(dx * dx + dy * dy);
      if (step < 0.5) return;
      const ctx = this.$refs.canvas.getContext("2d");
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      this.distance += step;
      this.points += 1;
      this.hasInk = true;
      this.lastPoint = point;
    },
    endStroke() {
      this.drawing = false;
      this.lastPoint = null;
    },
    pointerStart(event) {
      event.preventDefault();
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (this.$refs.canvas.setPointerCapture) {
        this.$refs.canvas.setPointerCapture(event.pointerId);
      }
      this.beginStroke(this.pointFromClient(event.clientX, event.clientY));
    },
    pointerMove(event) {
      if (!this.drawing) return;
      event.preventDefault();
      this.drawStroke(this.pointFromClient(event.clientX, event.clientY));
    },
    pointerEnd(event) {
      if (event) event.preventDefault();
      this.endStroke();
    },
    touchStart(event) {
      event.preventDefault();
      const touch = event.changedTouches[0];
      this.beginStroke(this.pointFromClient(touch.clientX, touch.clientY));
    },
    touchMove(event) {
      event.preventDefault();
      const touch = event.changedTouches[0];
      this.drawStroke(this.pointFromClient(touch.clientX, touch.clientY));
    },
    mouseStart(event) {
      if (event.button !== 0) return;
      event.preventDefault();
      this.beginStroke(this.pointFromClient(event.clientX, event.clientY));
    },
    mouseMove(event) {
      if (!this.drawing) return;
      event.preventDefault();
      this.drawStroke(this.pointFromClient(event.clientX, event.clientY));
    },
    isValid() {
      return this.hasInk && this.points >= 8 && this.distance >= 35;
    },
    clear() {
      this.hasInk = false;
      this.drawing = false;
      this.points = 0;
      this.distance = 0;
      this.lastPoint = null;
      this.prepareCanvas();
      this.$emit("clear");
    },
    exportBlob() {
      if (!this.isValid()) {
        return Promise.reject(new Error("请完整手写本人姓名"));
      }
      const canvas = this.$refs.canvas;
      return new Promise((resolve, reject) => {
        if (canvas.toBlob) {
          canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error("签名生成失败")), "image/png");
          return;
        }
        try {
          const parts = canvas.toDataURL("image/png").split(",");
          const binary = atob(parts[1]);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          resolve(new Blob([bytes], { type: "image/png" }));
        } catch (e) {
          reject(new Error("签名生成失败"));
        }
      });
    }
  }
};
</script>

<style scoped>
.signature-canvas-wrap {
  position: relative;
  height: 180px;
  overflow: hidden;
  border: 1px dashed #9ba9bc;
  border-radius: 8px;
  background: #fff;
}
.signature-canvas-wrap canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}
.signature-placeholder {
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  transform: translateY(-50%);
  color: #b5bdc9;
  font-size: 14px;
  text-align: center;
  pointer-events: none;
}
.signature-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  color: #8a94a3;
  font-size: 12px;
}
.signature-actions button {
  border: 0;
  padding: 6px 10px;
  color: #1677ff;
  background: #edf5ff;
  border-radius: 14px;
}
</style>
